import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import useAuth from 'hooks/useAuth';
import AnimateButton from 'ui-component/extended/AnimateButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Trans, useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { notifyErrors } from 'utils';
import Loader from 'ui-component/Loader';
import useConfig from 'hooks/useConfig';
const FirebaseLogin = ({ loginProp, ...others }: { loginProp?: number }) => {
    let { signin, checkSession } = useAuth();
    const { locale } = useConfig();
    let navigate = useNavigate();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    let [params] = useSearchParams();
    const getDefaultShape = useCallback(() => {
        return {
            email: Yup.string()
                .email(t('form_fields.validation_messages.field_is_invalid'))
                .max(255)
                .required(t('form_fields.validation_messages.field_is_required')),
            password: Yup.string().max(255).required(t('form_fields.validation_messages.field_is_required'))
        };
    }, [locale]);
    const [validationSchema, setValidationSchema] = useState(Yup.object().shape(getDefaultShape()));
    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            submit: null
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
            setSubmitting(true);
            if (signin)
                try {
                    await signin(values.email, values.password);
                    if (checkSession) {
                        await checkSession();
                    }
                    navigate('/');
                } catch (error) {
                    setStatus({ success: false });
                    if (error instanceof Error) {
                        setErrors({ submit: error.message });
                    }
                }
        }
    });
    const loginToken = !isEmpty(params.get('sp_lt')) ? params.get('sp_lt')?.replace(/\s/g, '+') : null;

    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.SyntheticEvent) => {
        event.preventDefault();
    };

    const [loading, setLoading] = useState<boolean>(loginToken ? true : false);

    const signInWithToken = useCallback(async () => {
        if (signin) {
            try {
                await signin('', '', loginToken);
                if (checkSession) {
                    await checkSession();
                }
                navigate('/');
            } catch (error) {
                notifyErrors(t('auth.login.login_token_error'));
                setLoading(false);
            }
        }
    }, [checkSession, loginToken, navigate, signin, t]);

    useEffect(() => {
        if (loginToken) {
            signInWithToken();
        } else {
            setLoading(false);
        }
    }, [loginToken, signInWithToken]);
    useEffect(() => {
        setValidationSchema(Yup.object().shape(getDefaultShape()));
        setTimeout(() => {
            formik.validateForm();
        }, 300);
    }, [locale]);

    if (loading) {
        return (
            <>
                <Loader />
                <Grid container direction="column" justifyContent="center" spacing={2}>
                    <Grid item xs={12} container alignItems="center" justifyContent="center">
                        <Stack alignItems="center" justifyContent="center" spacing={1}>
                            <Typography variant="caption" fontSize="16px" textAlign={matchDownSM ? 'center' : 'inherit'}>
                                <Trans>Just a moment, we're verifying your credentials...</Trans>
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </>
        );
    }

    return (
        <>
            <form noValidate onSubmit={formik.handleSubmit} {...others}>
                <Typography variant="body2" color="#132239" sx={{ mb: 1 }}>
                    <Trans>form_fields.labels.email</Trans>*
                </Typography>
                <FormControl fullWidth error={Boolean(formik.touched.email && formik.errors.email)} sx={{ mb: 3, borderRadius: '8px' }}>
                    <OutlinedInput
                        id="outlined-adornment-email-login"
                        type="email"
                        value={formik.values.email}
                        name="email"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        placeholder={t('form_fields.placeholders.email')}
                        sx={{
                            bgcolor: 'primary.light',
                            borderRadius: '8px',
                            '& fieldset': { border: 'none' },
                            '.MuiOutlinedInput-input': {
                                bgcolor: 'primary.light',
                                height: '32px',
                                color: '#132239'
                            }
                        }}
                    />
                    {formik.touched.email && formik.errors.email && <FormHelperText error>{formik.errors.email}</FormHelperText>}
                </FormControl>

                <Typography variant="body2" color="#132239" sx={{ mb: 1 }}>
                    <Trans>form_fields.labels.password</Trans>*
                </Typography>
                <FormControl
                    fullWidth
                    error={Boolean(formik.touched.password && formik.errors.password)}
                    sx={{ mb: 0.5, borderRadius: '8px' }}
                >
                    <OutlinedInput
                        id="outlined-adornment-password-login"
                        type={showPassword ? 'text' : 'password'}
                        value={formik.values.password}
                        name="password"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        placeholder={t('form_fields.placeholders.password')}
                        sx={{
                            bgcolor: 'primary.light',
                            borderRadius: '8px',
                            '& fieldset': { border: 'none' },
                            '.MuiOutlinedInput-input': {
                                bgcolor: 'primary.light',
                                height: '32px',
                                color: '#132239'
                            }
                        }}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <Visibility sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                    ) : (
                                        <VisibilityOff sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    {formik.touched.password && formik.errors.password && <FormHelperText error>{formik.errors.password}</FormHelperText>}
                </FormControl>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    {/* <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={(event) => setChecked(event.target.checked)}
                                        name="checked"
                                        color="primary"
                                        sx={{
                                            '.MuiSvgIcon-root': {
                                                width: '18px',
                                                height: '18px'
                                            },
                                            color: 'secondary.dark',
                                            '&.MuiCheckbox-root.Mui-checked': {
                                                color: 'primary.800'
                                            }
                                        }}
                                    />
                                }
                                label="Remember me"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '12px',
                                        color: '#132239'
                                    }
                                }}
                            /> */}
                    <Typography variant="body2" component={Link} to={loginProp ? `/forgot` : '/forgot'} color="primary.main">
                        <Trans>auth.login.forgot_password_q</Trans>
                    </Typography>
                </Stack>
                {formik.errors.submit && (
                    <Box sx={{ mt: 3 }}>
                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                    </Box>
                )}

                <Box sx={{ mt: 2 }}>
                    <AnimateButton>
                        <Button
                            disableElevation
                            disabled={formik.isSubmitting}
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            color="secondary"
                            sx={{
                                fontSize: '16px',
                                fontFamily: 'Rotonto',
                                fontWeight: 500,
                                backgroundColor: 'primary.800',
                                borderRadius: '8px'
                            }}
                        >
                            <Trans>auth.login.sign_in</Trans>
                        </Button>
                    </AnimateButton>
                </Box>
            </form>
        </>
    );
};

export default FirebaseLogin;
